import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Save, Plus, Trash2, Diamond,
  X, Check, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// ─── Constants ────────────────────────────────────────────────────────────────
const SHADES = ['Fancy', 'Lb', 'Lc', 'White'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const calcPieces = (weightCt, avgWeight) => {
  if (!weightCt || !avgWeight || avgWeight <= 0) return 0;
  return Math.round(parseFloat(weightCt) / avgWeight);
};

const sumCat = (cat) => {
  const sizes = cat.sizes || [];
  const wt = sizes.reduce((s, r) => s + (parseFloat(r.weight_ct) || 0), 0);
  const pcs = sizes.reduce((s, r) => s + (r.pieces || 0), 0);
  return { total_weight_ct: parseFloat(wt.toFixed(4)), total_pieces: Math.round(pcs) };
};

// ─── Sticky Header Component ─────────────────────────────────────────────────
function StickyHeader({ headerData, categories, step, totalSteps, onBack, onNext, onSave, saving }) {
  const [expanded, setExpanded] = useState(false);

  const grandWt = categories.reduce((s, c) => s + sumCat(c).total_weight_ct, 0);
  const grandPcs = categories.reduce((s, c) => s + sumCat(c).total_pieces, 0);

  const seriesLabel = [
    headerData.manual_series_no ? `#${headerData.manual_series_no}` : null,
    headerData.colour,
    headerData.clarity?.length ? headerData.clarity.join('/') : null,
    headerData.shade,
  ].filter(Boolean).join('  •  ');

  return (
    <div className="sticky top-0 z-20 bg-slate-900 text-white shadow-lg print:hidden">
      {/* Main bar */}
      <div className="px-4 py-2.5">
        {seriesLabel && (
          <p className="text-xs text-slate-400 mb-1 truncate">{seriesLabel}</p>
        )}
        <div className="flex items-center gap-3">
          {/* Back */}
          <button
            data-testid="wizard-back"
            onClick={onBack}
            disabled={step === 0}
            className="p-1.5 rounded-lg hover:bg-slate-700 disabled:opacity-30 transition-colors shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Totals */}
          <div className="flex-1 flex items-center gap-4">
            <div>
              <p className="text-[10px] text-slate-400 leading-none">Grand Total</p>
              <p className="font-bold text-base leading-tight">
                {grandWt.toFixed(3)} ct · {grandPcs.toLocaleString()} pcs
              </p>
            </div>
            {categories.length > 0 && (
              <button
                onClick={() => setExpanded(v => !v)}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
              >
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {categories.length} {categories.length === 1 ? 'cat' : 'cats'}
              </button>
            )}
          </div>

          {/* Step indicator */}
          <div className="text-xs text-slate-400 shrink-0">
            {step === 0 ? 'Step 1 — Info' : `Step ${step + 1} of ${totalSteps}`}
          </div>

          {/* Next */}
          <button
            data-testid="wizard-next"
            onClick={onNext}
            disabled={step >= totalSteps - 1}
            className="p-1.5 rounded-lg hover:bg-slate-700 disabled:opacity-30 transition-colors shrink-0"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Save */}
          <Button
            data-testid="wizard-save"
            onClick={onSave}
            disabled={saving}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-3 shrink-0"
          >
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
          </Button>
        </div>
      </div>

      {/* Expanded per-category breakdown */}
      {expanded && categories.length > 0 && (
        <div className="border-t border-slate-700 px-4 py-2 space-y-1 max-h-40 overflow-y-auto">
          {categories.map((cat, i) => {
            const { total_weight_ct, total_pieces } = sumCat(cat);
            return (
              <div key={i} className="flex justify-between text-xs text-slate-300">
                <span className="truncate max-w-[55%]">{cat.category_name || `Cat ${i + 1}`}</span>
                <span>{total_weight_ct.toFixed(3)} ct · {total_pieces.toLocaleString()} pcs</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Phase 1 — Header Info ────────────────────────────────────────────────────
function HeaderStep({ data, onChange, colours, clarities, shapeTypes }) {
  const toggleClarity = (name) => {
    const current = data.clarity || [];
    if (current.includes(name)) {
      onChange('clarity', current.filter(c => c !== name));
    } else {
      onChange('clarity', [...current, name]);
    }
  };

  return (
    <div className="p-4 space-y-5 pb-24">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Series Information</h2>
        <p className="text-sm text-gray-500">Fill in the series details before adding categories</p>
      </div>

      {/* Series No */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Series No <span className="text-red-500">*</span></label>
        <Input
          data-testid="header-series-no"
          value={data.manual_series_no}
          onChange={e => onChange('manual_series_no', e.target.value)}
          placeholder="e.g. 42"
          className="h-11"
        />
      </div>

      {/* Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Date of Issue</label>
        <Input
          data-testid="header-date"
          type="date"
          value={data.date_of_issue}
          onChange={e => onChange('date_of_issue', e.target.value)}
          className="h-11"
        />
      </div>

      {/* Colour */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Colour <span className="text-red-500">*</span></label>
        <select
          data-testid="header-colour"
          value={data.colour}
          onChange={e => onChange('colour', e.target.value)}
          className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select colour...</option>
          {colours.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Clarity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Clarity (select multiple)</label>
        <div className="flex flex-wrap gap-2" data-testid="clarity-chips">
          {clarities.map(c => {
            const selected = (data.clarity || []).includes(c);
            return (
              <button
                key={c}
                data-testid={`clarity-chip-${c}`}
                onClick={() => toggleClarity(c)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  selected
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                }`}
              >
                {selected && <Check className="w-3 h-3 inline mr-1" />}
                {c}
              </button>
            );
          })}
        </div>
      </div>

      {/* Shade */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Shade <span className="text-red-500">*</span></label>
        <div className="grid grid-cols-4 gap-2" data-testid="shade-pills">
          {SHADES.map(s => (
            <button
              key={s}
              data-testid={`shade-${s}`}
              onClick={() => onChange('shade', s)}
              className={`py-2.5 rounded-lg text-sm font-semibold border-2 transition-all ${
                data.shade === s
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Shape Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Shape Type</label>
        <select
          data-testid="header-shape"
          value={data.shape_type_id}
          onChange={e => {
            const opt = shapeTypes.find(s => s.id === e.target.value);
            onChange('shape_type_id', e.target.value);
            onChange('shape_type_name', opt?.name || '');
          }}
          className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select shape...</option>
          {shapeTypes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Codes Used */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Codes Used</label>
        <Input
          data-testid="header-codes"
          value={data.codes_used}
          onChange={e => onChange('codes_used', e.target.value)}
          placeholder="e.g. ABC-123"
          className="h-11"
        />
      </div>
    </div>
  );
}

// ─── Category Row (one sieve size entry) ─────────────────────────────────────
function SieveRow({ row, sieveSizes, onWeightChange, onSieveChange, onRemove, index }) {
  const selectedSieve = sieveSizes.find(s => s.id === row.sieve_size_id);
  const avgWt = selectedSieve?.avg_weight_ct || 0;
  const pieces = calcPieces(row.weight_ct, avgWt);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-2">
      {/* Sieve size dropdown */}
      <div className="flex-1 min-w-0">
        <select
          data-testid={`sieve-select-${index}`}
          value={row.sieve_size_id}
          onChange={e => {
            const s = sieveSizes.find(x => x.id === e.target.value);
            onSieveChange(index, e.target.value, s?.label || '', s?.avg_weight_ct || 0);
          }}
          className="w-full h-9 px-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select size...</option>
          {sieveSizes.map(s => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        {avgWt > 0 && (
          <p className="text-[10px] text-gray-400 mt-0.5 ml-1">avg {avgWt} ct/pc</p>
        )}
      </div>

      {/* Weight input */}
      <div className="w-24 shrink-0">
        <Input
          data-testid={`sieve-weight-${index}`}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.001"
          value={row.weight_ct === '' ? '' : row.weight_ct}
          onChange={e => onWeightChange(index, e.target.value)}
          placeholder="0.000"
          className="h-9 text-sm text-right"
        />
        <p className="text-[10px] text-gray-400 text-right mt-0.5">ct</p>
      </div>

      {/* Pieces (read-only) */}
      <div className="w-16 shrink-0 text-right">
        <p className="font-semibold text-gray-900 text-sm">{pieces > 0 ? pieces.toLocaleString() : '—'}</p>
        <p className="text-[10px] text-gray-400">pcs</p>
      </div>

      {/* Remove */}
      <button
        data-testid={`remove-sieve-${index}`}
        onClick={() => onRemove(index)}
        className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Phase 2+ — Category Screen ───────────────────────────────────────────────
function CategoryStep({ catIndex, cat, allCategories, sievesByCategory, onCatChange, onAddRow, onRemoveRow, onWeightChange, onSieveChange, onRemoveCategory, isOnly }) {
  const sieveSizes = sievesByCategory[cat.category_id] || [];
  const { total_weight_ct, total_pieces } = sumCat(cat);

  const handleCategorySelect = (catId) => {
    const found = allCategories.find(c => c.id === catId);
    onCatChange(catIndex, 'category_id', catId);
    onCatChange(catIndex, 'category_name', found?.name || '');
  };

  return (
    <div className="p-4 space-y-4 pb-32">
      {/* Category header */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
          <select
            data-testid={`cat-select-${catIndex}`}
            value={cat.category_id}
            onChange={e => handleCategorySelect(e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-lg bg-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select category...</option>
            {allCategories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {!isOnly && (
          <button
            data-testid={`remove-cat-${catIndex}`}
            onClick={() => onRemoveCategory(catIndex)}
            className="mt-6 p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Category totals bar */}
      {(total_weight_ct > 0 || total_pieces > 0) && (
        <div className="bg-blue-50 rounded-xl px-4 py-2.5 flex gap-6">
          <div>
            <p className="text-[10px] text-blue-500 font-medium">Cat Total</p>
            <p className="font-bold text-blue-800">{total_weight_ct.toFixed(3)} ct</p>
          </div>
          <div>
            <p className="text-[10px] text-blue-500 font-medium">Pieces</p>
            <p className="font-bold text-blue-800">{total_pieces.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Column headers */}
      {cat.sizes.length > 0 && (
        <div className="grid grid-cols-[1fr_96px_64px_36px] gap-2 px-1">
          <p className="text-xs text-gray-400 font-medium">Sieve Size</p>
          <p className="text-xs text-gray-400 font-medium text-right">Carats</p>
          <p className="text-xs text-gray-400 font-medium text-right">Pieces</p>
          <span />
        </div>
      )}

      {/* Size rows */}
      <div className="space-y-2">
        {cat.sizes.map((row, rowIdx) => (
          <SieveRow
            key={rowIdx}
            row={row}
            index={rowIdx}
            sieveSizes={sieveSizes}
            onWeightChange={(i, v) => onWeightChange(catIndex, i, v)}
            onSieveChange={(i, id, label, avg) => onSieveChange(catIndex, i, id, label, avg)}
            onRemove={i => onRemoveRow(catIndex, i)}
          />
        ))}
      </div>

      {/* Add sieve size */}
      {cat.category_id && (
        <button
          data-testid={`add-sieve-${catIndex}`}
          onClick={() => onAddRow(catIndex)}
          className="w-full py-3 rounded-xl border-2 border-dashed border-blue-300 text-blue-600 text-sm font-medium hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Sieve Size
        </button>
      )}
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────
const EMPTY_ROW = () => ({ sieve_size_id: '', sieve_size_label: '', avg_weight_ct: 0, weight_ct: '', pieces: 0 });
const EMPTY_CAT = () => ({ category_id: '', category_name: '', sizes: [EMPTY_ROW()] });

export default function DiamondSeriesForm() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Masters
  const [colours, setColours] = useState([]);
  const [clarities, setClarities] = useState([]);
  const [shapeTypes, setShapeTypes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allSieveSizes, setAllSieveSizes] = useState([]);

  // Form state
  const [headerData, setHeaderData] = useState({
    manual_series_no: '',
    colour: '',
    clarity: [],
    shade: '',
    codes_used: '',
    shape_type_id: '',
    shape_type_name: '',
    date_of_issue: new Date().toISOString().split('T')[0],
  });
  const [categories, setCategories] = useState([EMPTY_CAT()]);

  // Sieve sizes grouped by category_id
  const sievesByCategory = useMemo(() => {
    const map = {};
    allSieveSizes.forEach(s => {
      if (!map[s.category_id]) map[s.category_id] = [];
      map[s.category_id].push(s);
    });
    return map;
  }, [allSieveSizes]);

  const totalSteps = 1 + categories.length; // step 0 = header, 1..N = categories

  // Load masters
  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, clRes, stRes, catRes, sieveRes] = await Promise.all([
          api.get('/catalog/diamond-colours'),
          api.get('/catalog/diamond-clarities'),
          api.get('/diamond-series/masters/shape-types'),
          api.get('/diamond-series/masters/categories'),
          api.get('/diamond-series/masters/sieve-sizes'),
        ]);
        setColours(cRes.data.map(i => i.name));
        setClarities(clRes.data.map(i => (typeof i === 'string' ? i : i.name)));
        setShapeTypes(stRes.data);
        setAllCategories(catRes.data);
        setAllSieveSizes(sieveRes.data);
      } catch (e) {
        toast.error('Failed to load masters');
      }
    };
    load();
  }, []);

  const handleHeaderChange = (key, value) => {
    setHeaderData(prev => ({ ...prev, [key]: value }));
  };

  const handleCatChange = (catIdx, key, value) => {
    setCategories(prev => {
      const updated = [...prev];
      updated[catIdx] = { ...updated[catIdx], [key]: value };
      return updated;
    });
  };

  const handleAddRow = (catIdx) => {
    setCategories(prev => {
      const updated = [...prev];
      updated[catIdx] = { ...updated[catIdx], sizes: [...updated[catIdx].sizes, EMPTY_ROW()] };
      return updated;
    });
  };

  const handleRemoveRow = (catIdx, rowIdx) => {
    setCategories(prev => {
      const updated = [...prev];
      const sizes = updated[catIdx].sizes.filter((_, i) => i !== rowIdx);
      updated[catIdx] = { ...updated[catIdx], sizes: sizes.length ? sizes : [EMPTY_ROW()] };
      return updated;
    });
  };

  const handleWeightChange = (catIdx, rowIdx, value) => {
    setCategories(prev => {
      const updated = [...prev];
      const sizes = [...updated[catIdx].sizes];
      const avg = sizes[rowIdx].avg_weight_ct || 0;
      sizes[rowIdx] = {
        ...sizes[rowIdx],
        weight_ct: value,
        pieces: calcPieces(value, avg),
      };
      updated[catIdx] = { ...updated[catIdx], sizes };
      return updated;
    });
  };

  const handleSieveChange = (catIdx, rowIdx, sieveId, label, avgWt) => {
    setCategories(prev => {
      const updated = [...prev];
      const sizes = [...updated[catIdx].sizes];
      const currentWt = sizes[rowIdx].weight_ct;
      sizes[rowIdx] = {
        ...sizes[rowIdx],
        sieve_size_id: sieveId,
        sieve_size_label: label,
        avg_weight_ct: avgWt,
        pieces: calcPieces(currentWt, avgWt),
      };
      updated[catIdx] = { ...updated[catIdx], sizes };
      return updated;
    });
  };

  const handleAddCategory = () => {
    setCategories(prev => [...prev, EMPTY_CAT()]);
    setStep(categories.length + 1); // Navigate to the new category step
  };

  const handleRemoveCategory = (catIdx) => {
    if (categories.length <= 1) return;
    setCategories(prev => prev.filter((_, i) => i !== catIdx));
    if (step > catIdx) setStep(prev => Math.max(1, prev - 1));
  };

  const handleBack = () => setStep(prev => Math.max(0, prev - 1));
  const handleNext = () => setStep(prev => Math.min(totalSteps - 1, prev + 1));

  const validate = () => {
    if (!headerData.manual_series_no.trim()) { toast.error('Series No is required'); return false; }
    if (!headerData.colour) { toast.error('Colour is required'); return false; }
    if (!headerData.shade) { toast.error('Shade is required'); return false; }
    for (let i = 0; i < categories.length; i++) {
      if (!categories[i].category_id) { toast.error(`Please select a category for step ${i + 2}`); setStep(i + 1); return false; }
      const hasData = categories[i].sizes.some(s => s.sieve_size_id && parseFloat(s.weight_ct) > 0);
      if (!hasData) { toast.error(`Category ${i + 1} has no sieve size data`); setStep(i + 1); return false; }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const preparedCats = categories.map(cat => {
        const validSizes = cat.sizes.filter(s => s.sieve_size_id && parseFloat(s.weight_ct) > 0);
        const { total_weight_ct, total_pieces } = sumCat({ sizes: validSizes });
        return {
          category_id: cat.category_id,
          category_name: cat.category_name,
          sizes: validSizes.map(s => ({
            sieve_size_id: s.sieve_size_id,
            sieve_size_label: s.sieve_size_label,
            avg_weight_ct: s.avg_weight_ct,
            weight_ct: parseFloat(s.weight_ct) || 0,
            pieces: s.pieces || 0,
          })),
          total_weight_ct,
          total_pieces,
        };
      });

      const grandWt = preparedCats.reduce((s, c) => s + c.total_weight_ct, 0);
      const grandPcs = preparedCats.reduce((s, c) => s + c.total_pieces, 0);

      const payload = {
        ...headerData,
        categories: preparedCats,
        grand_total_weight_ct: parseFloat(grandWt.toFixed(4)),
        grand_total_pieces: Math.round(grandPcs),
      };

      const res = await api.post('/diamond-series', payload);
      toast.success(`Series ${res.data.series_number} saved!`);
      navigate(`/diamond-series/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 print:hidden">
        <button onClick={() => navigate('/diamond-series')} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex items-center gap-2">
          <Diamond className="w-4 h-4 text-blue-600" />
          <h1 className="text-base font-semibold text-gray-900">New Diamond Series</h1>
        </div>
      </div>

      {/* Sticky header (show from step 0 to track progress) */}
      <StickyHeader
        headerData={headerData}
        categories={categories}
        step={step}
        totalSteps={totalSteps}
        onBack={handleBack}
        onNext={handleNext}
        onSave={handleSave}
        saving={saving}
      />

      {/* Content */}
      <div className="flex-1 max-w-2xl w-full mx-auto">
        {step === 0 ? (
          <HeaderStep
            data={headerData}
            onChange={handleHeaderChange}
            colours={colours}
            clarities={clarities}
            shapeTypes={shapeTypes}
          />
        ) : (
          <CategoryStep
            catIndex={step - 1}
            cat={categories[step - 1]}
            allCategories={allCategories}
            sievesByCategory={sievesByCategory}
            onCatChange={handleCatChange}
            onAddRow={handleAddRow}
            onRemoveRow={handleRemoveRow}
            onWeightChange={handleWeightChange}
            onSieveChange={handleSieveChange}
            onRemoveCategory={handleRemoveCategory}
            isOnly={categories.length === 1}
          />
        )}
      </div>

      {/* Bottom action bar */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 print:hidden">
        {step === 0 ? (
          <Button
            data-testid="goto-categories"
            onClick={() => setStep(1)}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base"
          >
            Next — Add Categories
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        ) : (
          <button
            data-testid="add-category-btn"
            onClick={handleAddCategory}
            className="w-full py-3 rounded-xl border-2 border-dashed border-emerald-400 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Another Category
          </button>
        )}
      </div>
    </div>
  );
}
